import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";

const BANNER = String.raw`
   ____            _ ____                      _
  / __ \____ ___  (_) ___/___  ____ ___________/ /_
 / / / / __ \`__ \/ /\__ \/ _ \/ __ \`/ ___/ ___/ __ \
/ /_/ / / / / / / /___/ /  __/ /_/ / /  / /__/ / / /
\____/_/ /_/ /_/_//____/\___/\__,_/_/   \___/_/ /_/
            ::  hack-zone  ::  v1.0  ::
`;

function MatrixRain() {
  const ref = useRef(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let w = (canvas.width = window.innerWidth);
    let h = (canvas.height = window.innerHeight);
    const chars = "アァカサタナハマヤラワABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789".split("");
    const fs = 14;
    let columns = Math.floor(w / fs);
    let drops = Array(columns).fill(1);
    const draw = () => {
      ctx.fillStyle = "rgba(5,5,5,0.07)";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = "#00ff41";
      ctx.font = `${fs}px JetBrains Mono, monospace`;
      for (let i = 0; i < drops.length; i++) {
        const t = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(t, i * fs, drops[i] * fs);
        if (drops[i] * fs > h && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };
    const id = setInterval(draw, 55);
    const onR = () => {
      w = canvas.width = window.innerWidth;
      h = canvas.height = window.innerHeight;
      columns = Math.floor(w / fs);
      drops = Array(columns).fill(1);
    };
    window.addEventListener("resize", onR);
    return () => { clearInterval(id); window.removeEventListener("resize", onR); };
  }, []);
  return <canvas ref={ref} className="matrix-rain" />;
}

export default function HackSecretPage() {
  const [lines, setLines] = useState([
    { kind: "out", text: BANNER },
    { kind: "out", text: "Welcome, intruder. Type 'help' to see what you can do.\nType 'flag' to claim your prize. Type 'exit' to leave (you can't)." },
  ]);
  const [cmd, setCmd] = useState("");
  const [history, setHistory] = useState([]);
  const [hIdx, setHIdx] = useState(-1);
  const inputRef = useRef(null);
  const endRef = useRef(null);

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [lines]);

  const exec = async (raw) => {
    const text = raw.trim();
    setLines((l) => [...l, { kind: "cmd", text: raw }]);
    if (!text) return;
    setHistory((h) => [text, ...h]);
    setHIdx(-1);
    if (text === "clear") { setLines([]); return; }
    if (text === "home") { window.location.href = "/"; return; }
    try {
      const { data } = await api.post("/hack/exec", { cmd: text });
      setLines((l) => [...l, { kind: "out", text: data.output }]);
    } catch {
      setLines((l) => [...l, { kind: "err", text: "connection error" }]);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      exec(cmd);
      setCmd("");
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      const ni = Math.min(history.length - 1, hIdx + 1);
      if (history[ni] !== undefined) { setHIdx(ni); setCmd(history[ni]); }
    } else if (e.key === "ArrowDown") {
      e.preventDefault();
      const ni = Math.max(-1, hIdx - 1);
      setHIdx(ni);
      setCmd(ni === -1 ? "" : history[ni]);
    }
  };

  return (
    <div className="hack-screen" onClick={() => inputRef.current?.focus()} data-testid="hack-secret-page">
      <MatrixRain />
      <div className="relative z-10 p-4 sm:p-8 min-h-screen flex flex-col" style={{ filter: "drop-shadow(0 0 1px rgba(0,255,65,0.3))" }}>
        <div className="flex items-center justify-between mb-4">
          <div className="hack-glow text-xs">root@omnisearch:~#</div>
          <Link to="/" className="hack-glow text-xs hover:underline" data-testid="hack-exit-link">[ exit to surface ]</Link>
        </div>
        <div className="flex-1 whitespace-pre-wrap text-sm leading-relaxed">
          {lines.map((l, i) => (
            <div key={i} className={l.kind === "cmd" ? "" : "hack-glow"}>
              {l.kind === "cmd" ? <span><span className="opacity-60">$</span> {l.text}</span> : l.text}
            </div>
          ))}
          <div ref={endRef} />
        </div>
        <div className="mt-4 flex items-center text-sm">
          <span className="opacity-60 mr-2">$</span>
          <input
            ref={inputRef}
            value={cmd}
            onChange={(e) => setCmd(e.target.value)}
            onKeyDown={onKey}
            data-testid="hack-cmd-input"
            className="bg-transparent border-0 outline-none flex-1 text-[#00ff41] font-mono hack-glow"
            autoComplete="off"
            spellCheck="false"
          />
          <span className="hack-cursor" />
        </div>
        <div className="mt-3 text-[10px] opacity-50">
          shortcuts: help · ls · cat secrets.txt · matrix · konami · flag · clear · home
        </div>
      </div>
    </div>
  );
}
